import { useState, MouseEvent, ChangeEvent } from "react";
import { Box, Popover, popoverClasses } from "@mui/material";
import { Typography, palette, Select } from "@powerdiary/components-library/lib";
import { ChevronDown, X } from "lucide-react";
import { BookingType, IProviderData, IGroupData } from "types/appointmentBooking";
import { ISlot } from "../../AvailabilityShared.helpers";
import { IdNameItemModel, ProviderModel } from "types/generated/models";

interface ITimeSlotButtonProps {
    slot: ISlot;
    isSelected: boolean;
    backgroundColor: string;
    textColor: string;
    timeOfDay: "morning" | "afternoon" | "evening";
    onClick: (slot: ISlot, selectedProvider?: IdNameItemModel) => void;
    provider: IProviderData;
    availableProviders: ProviderModel[];
    bookingType: BookingType;
    defaultStripeAccount: string;
    onProviderSelect: (providerData: IProviderData) => void;
    onGroupSelect: (groupData: IGroupData) => void;
}

export default function TimeSlotButton({
    slot,
    isSelected,
    backgroundColor,
    textColor,
    timeOfDay,
    onClick,
    provider,
    availableProviders,
    bookingType,
    defaultStripeAccount,
    onProviderSelect,
    onGroupSelect,
}: Readonly<ITimeSlotButtonProps>) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const isExpanded = Boolean(anchorEl);

    const isAllPractitionersSelected = provider.providerId === 0;
    const hasMultipleProviders = slot.providers && slot.providers.length > 1;
    const shouldShowDropdown =
        (isAllPractitionersSelected || hasMultipleProviders) && bookingType === BookingType.CLIENT;

    const getSelectedColors = () => {
        if (!isSelected) return { bg: backgroundColor, text: textColor };

        switch (timeOfDay) {
            case "morning":
                return { bg: palette.dusk["700"], text: "white" };
            case "afternoon":
                return { bg: palette.sunflower["900"], text: "white" };
            case "evening":
                return { bg: palette.twilight["800"], text: "white" };
            default:
                return { bg: palette.dusk["700"], text: "white" };
        }
    };

    const colors = getSelectedColors();
    const getSelectedBackgroundColor = (timeOfDay: string) => {
        if (timeOfDay === "morning") return palette.dusk["700"];
        if (timeOfDay === "afternoon") return palette.sunflower["900"];
        return palette.twilight["800"];
    };
    const selectedColors = {
        bg: getSelectedBackgroundColor(timeOfDay),
        text: "white",
    };

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        if (shouldShowDropdown) {
            setAnchorEl(event.currentTarget);
        } else {
            if (bookingType === BookingType.CLASS && slot.name && slot.id) {
                if (isAllPractitionersSelected && slot.providerId) {
                    const actualProvider = availableProviders?.find((p) => p.id === slot.providerId);
                    const actualProviderStripeAccount = availableProviders?.find(
                        (p) => p.id === slot.providerId
                    )?.stripeAccount;

                    const providerData: IProviderData = {
                        providerId: slot.providerId,
                        providerName: actualProvider?.name || "",
                        providerStripeAccount: actualProviderStripeAccount ?? defaultStripeAccount,
                    };
                    onProviderSelect(providerData);
                }

                const groupData: IGroupData = {
                    groupId: slot.id,
                    groupName: slot.name,
                };
                onGroupSelect(groupData);
            }
            onClick(slot);
        }
    };

    const handleProviderSelect = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const selectedProviderId = event.target.value;
        const selectedProviderSlot = slot.providers?.find((p) => p.id?.toString() === selectedProviderId);
        const selectedProviderStripeAccount = availableProviders?.find(
            (p) => p.id?.toString() === selectedProviderId
        )?.stripeAccount;
        if (selectedProviderSlot) {
            const providerData: IProviderData = {
                providerId: selectedProviderSlot.id,
                providerName: selectedProviderSlot.name,
                providerStripeAccount: selectedProviderStripeAccount ?? defaultStripeAccount,
            };

            onProviderSelect(providerData);

            if (bookingType === BookingType.CLASS && slot.name && slot.id) {
                const groupData: IGroupData = {
                    groupId: slot.id,
                    groupName: slot.name,
                };
                onGroupSelect(groupData);
            }

            onClick(slot, selectedProviderSlot);
        }
        setAnchorEl(null);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const providerOptions =
        slot.providers?.map((provider) => ({
            label: provider.name || "",
            value: provider.id?.toString() || "",
        })) || [];

    const getSlotDisplayText = () => {
        const timeText = slot.date.toFormat("h:mm a");

        if (bookingType === BookingType.CLASS) {
            const className = slot.name || "";
            return `${timeText} ${className} ${isAllPractitionersSelected ? "" : provider?.providerName || ""}`.trim();
        }

        return timeText;
    };

    const displayText = getSlotDisplayText();

    return (
        <Box sx={{ position: "relative" }}>
            <Box
                onClick={handleClick}
                sx={{
                    padding: "4px 8px",
                    width: bookingType === BookingType.CLASS ? "auto" : "82px",
                    minWidth: bookingType === BookingType.CLASS ? "120px" : "82px",
                    borderRadius: "4px",
                    backgroundColor: colors.bg,
                    color: colors.text,
                    cursor: "pointer",
                    textAlign: "center",
                    whiteSpace: bookingType === BookingType.CLASS ? "normal" : "nowrap",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                }}
                data-test-id={`time_slot_${timeOfDay}_${slot.id}`}
            >
                <Typography variant="paragraphSmallSemiBold">{displayText}</Typography>
                {shouldShowDropdown && (
                    <ChevronDown size={14} strokeWidth={2} aria-hidden data-test-id="icon_time_slot_dropdown_chevron" />
                )}
            </Box>

            <Popover
                open={shouldShowDropdown && isExpanded}
                anchorEl={anchorEl}
                onClose={handleClose}
                disableRestoreFocus={true}
                disableEnforceFocus={true}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                disablePortal={false}
                sx={{
                    [`& .${popoverClasses.paper}`]: {
                        minWidth: "200px",
                        borderRadius: "4px",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                        overflow: "hidden",
                        mt: 0,
                    },
                }}
            >
                <Box
                    sx={{
                        backgroundColor: selectedColors.bg,
                        color: selectedColors.text,
                        padding: "8px 12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography variant="paragraphSmallSemiBold">{displayText}</Typography>
                    <Box
                        component="button"
                        type="button"
                        onClick={handleClose}
                        aria-label="Close provider selection"
                        data-test-id="button_time_slot_popover_close"
                        sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "16px",
                            height: "16px",
                            border: 0,
                            padding: 0,
                            background: "transparent",
                            color: "inherit",
                        }}
                    >
                        <X size={14} strokeWidth={2} aria-hidden />
                    </Box>
                </Box>

                <Box
                    sx={{
                        backgroundColor: "white",
                        padding: "12px",
                    }}
                >
                    <Select
                        options={providerOptions}
                        size="small"
                        label="Select Provider"
                        placeholder="Select Provider"
                        onChange={handleProviderSelect}
                        sx={{ width: "100%" }}
                    />
                </Box>
            </Popover>
        </Box>
    );
}
